import { Controller } from 'core/Controller';
import { Events } from 'core/events/Events';
import { AppEvents } from 'app/events/AppEvents';
import { SectionType, DepartmentType } from 'app/data/Enums';
import { HistoryStateModel } from 'app/model/HistoryStateModel';
import { AppController } from 'app/controller/AppController';
import { NavMenuController } from 'app/chrome/navmenu/NavMenuController';
import type { MouseController } from 'app/chrome/mouse/MouseController';
import Config from 'config/settings';
import { Simple } from 'utils/analytics';
import { UIController } from 'app/views/ui/UIController';
import { AboutController } from 'app/views/about/AboutController';


export class ProjectsMenuController extends Controller {
	private _$body;
	private _$showButtonText;
	private _app: AppController;
	private _mouse: MouseController;
	private _about: AboutController;
	private _ui: UIController;
	private _menuShowing: boolean = false;

	private _currentSelectTarget: HTMLElement;
	private _selectedDepartment: DepartmentType;
	private _allowClick: boolean = true;
	private _interactionInt: number;
	private _navmenuController: NavMenuController;
	private _$disableHoverArea: HTMLElement;
	private _allowHover: boolean = true;
	//Current history state — write-only (set in setState, never read; public so
	//noUnusedLocals doesn't flag it):
	public _state: HistoryStateModel;
	//false until first use, then holds a native setTimeout id:
	private _enableHoverInt: number | boolean = false;
	private _touchAutoPlayInterval: number;
	private _currentTouchPlayIndex: number = 0;
	private _menuItems: HTMLElement[] = [];

	public init(): void {}

	public setControllers(
		app: AppController,
		navmenu: NavMenuController,
		mouse: MouseController,
		ui: UIController,
		about: AboutController
	) {
		this._app = app;
		this._navmenuController = navmenu;
		this._mouse = mouse;
		this._ui = ui;
		this._about = about;
	}

	public setSelectedDepartment(department, updateStates: boolean = false, isDeepLink: boolean = false) {
		this._selectedDepartment = department;

		let buttonLabel = 'Projects';
		if (this._selectedDepartment) {
			buttonLabel = isDeepLink ? 'Filter' : 'Filter';
		}

		setTimeout(() => {
			this.DOM.setHTML(this._$showButtonText, buttonLabel);
		}, 800);

		if (updateStates) {
			if (this._currentSelectTarget) {
				this.DOM.removeClass(this._currentSelectTarget, '-selected');
			}

			this._currentSelectTarget = this.container.querySelector('.projects__menu__item.-' + this._selectedDepartment);
			this.DOM.addClass(this._currentSelectTarget, '-selected');
		}
	}

	public DOMReady(): void {

		if (!this.container) {
			return;
		}

		if (this._selectedDepartment) {
			this._currentSelectTarget = this.container.querySelector('.projects__menu__item.-' + this._selectedDepartment);
			this.DOM.addClass(this._currentSelectTarget, '-selected');
		}

		this.container.querySelector('.navmenu__background');
		this._$body = document.querySelector('body');
		this._$showButtonText = document.querySelector('.show__projects__button__text.-projects');
		this._$disableHoverArea = document.querySelector('.projects__menu__hidearea__disable');

		// Store menu items for touch auto-play
		this._menuItems = Array.from(this.container.querySelectorAll('.projects__menu__item'));

		Events.on('click', '.show__projects__button', (e: MouseEvent) => {
			e.preventDefault();

			if (!this._allowClick) return false;

			if (this._menuShowing) {
				this._hideMenu();
				Simple.event('hide_project_menu', { source: 'button' });
			} else {
				Simple.event('show_project_menu');
				this._showMenu();
			}
			return false;
		});

		Events.on('mouseover', '.projects__menu__hidearea__disable', (e: MouseEvent) => {
			this._allowHover = true;
			this.DOM.removeClass(this._$disableHoverArea, '-active');
			clearTimeout(this._enableHoverInt as number);
		});

		if (Config.doProjectsButtonMouseOver) {
			Events.on('mouseover', '.show__projects__button', (e: MouseEvent) => {
				if (this._menuShowing || !this._allowClick || !this._allowHover) {
					return false;
				}

				this._showMenu();
			});
		}

		Events.on('mouseover', '.projects__menu__hidearea', (e: MouseEvent) => {
			Simple.event('hide_project_menu', { source: 'rollout' });
			this._hideMenu();
		});

		Events.on('click', '.ui_close__button', (e: MouseEvent) => {
			e.preventDefault();

			if (!this._menuShowing) {
				return;
			}

			this._navmenuController.delayInfoButtonInteraction();
			this._hideMenu(true);
			Simple.event('hide_project_menu', { source: 'close_button' });

			return false;
		});

		let video;
		Events.on('click', '.projects__menu__item', (e: MouseEvent) => {
			e.preventDefault();

			if (this._currentSelectTarget) {
				this.DOM.removeClass(this._currentSelectTarget, '-selected');
			}

			let $target = e.target as HTMLElement;
			this.DOM.addClass($target, '-selected');
			this._currentSelectTarget = $target;


			let department = $target.dataset.department;
			let title;

			if (department === DepartmentType.SCREENS) title = 'Screens';
			else if (department === DepartmentType.SPACES) title = 'Spaces';
			else if (department === DepartmentType.SYSTEMS) title = 'Systems';

			//Trigger event to switch to 'work' controller for department:
			let newState: HistoryStateModel = {
				section: SectionType.WORK,
				subsection: department,
				title: title,
				href: `/${department}`,
				workIndex: 0
			};
			Events.dispatch(AppEvents.CHANGE_SECTION, newState);
			this._app.unpauseActive();

			Simple.event('click_project_menu_item', { department: department });

			return false;
		});

		Events.on('mouseover', '.projects__menu__item', (e: MouseEvent) => {
			video =
				(e.target as HTMLElement).querySelector('video') ||
				(e.target as HTMLElement).parentElement.querySelector('video');
			video.play();
			this.DOM.addClass(this._$body, 'projectitem-hover');

			let $target = e.target as HTMLElement;
			let department = $target.dataset.department;
			Simple.event('mouseover_project_menu_item', { department: department });
		});

		Events.on('mouseout', '.projects__menu__item', (e: MouseEvent) => {
			video.pause();
			this.DOM.removeClass(this._$body, 'projectitem-hover');
		});
	}

	public isShowing() {
		return this._menuShowing;
	}

	public hide(changeSection = false) {
		this._hideMenu(true, changeSection);
	}

	/** Section has change so set state */
	public setState(state: HistoryStateModel, updateMenuState: boolean = true): void {
		this._state = state;
		if (updateMenuState) {
		}
	}

	public disableHoverForTransition() {
		if (!this._mouse.lastPointerWasTouch()) {
			this._allowHover = false;
			this.DOM.addClass(this._$disableHoverArea, '-active');
			clearTimeout(this._enableHoverInt as number);
			this._enableHoverInt = setTimeout(() => {
				this._allowHover = true;
				this.DOM.removeClass(this._$disableHoverArea, '-active');
			}, 4000);
		}
	}

	//PRIVATE
	//----------------------------------------------------------------------------
	//----------------------------------------------------------------------------
	//----------------------------------------------------------------------------
	//----------------------------------------------------------------------------

	private _addInteractionDelay() {
		this._allowClick = false;
		clearTimeout(this._interactionInt);
		this._interactionInt = setTimeout(() => {
			this._allowClick = true;
		}, 1000);
	}

	private _showMenu(): void {
		this._about.resetScrollProgress();
		this._addInteractionDelay();
		this._app.pauseActive();
		this._menuShowing = true;
		this.DOM.addClass(this._$body, '-projectsmenushowing');

		// Start touch auto-play if in touch mode
		if (this._mouse.lastPointerWasTouch()) {
			this._startTouchAutoPlay();
		}
	}

	private _hideMenu(addDelay = true, fromChangeSection = false): void {
		if (addDelay) this._addInteractionDelay();

		// Stop touch auto-play
		this._stopTouchAutoPlay();

		this._ui.hideUiItem('info');

		setTimeout(() => {
			this._ui.showUiItem('info');
		}, 700);

		if (!fromChangeSection) this._app.unpauseActive();
		this._menuShowing = false;
		this.DOM.removeClass(this._$body, '-projectsmenushowing');
	}

	private _startTouchAutoPlay(): void {
		if (this._menuItems.length === 0) return;

		// Start with first item
		this._currentTouchPlayIndex = 0;
		this._playTouchItem(this._currentTouchPlayIndex);

		// Set up interval to cycle through items
		this._touchAutoPlayInterval = window.setInterval(() => {
			// Pause and remove class from current item
			const currentItem = this._menuItems[this._currentTouchPlayIndex];
			if (currentItem) {
				const currentVideo = currentItem.querySelector('video');
				if (currentVideo) {
					currentVideo.pause();
				}
				this.DOM.removeClass(currentItem, '-touchplaying');
			}

			// Move to next item (loop back to 0 after last item)
			this._currentTouchPlayIndex = (this._currentTouchPlayIndex + 1) % this._menuItems.length;

			// Play next item
			this._playTouchItem(this._currentTouchPlayIndex);
		}, 3000);
	}

	private _playTouchItem(index: number): void {
		const item = this._menuItems[index];
		if (!item) return;

		const video = item.querySelector('video');
		if (video) {
			video.play();
		}
		this.DOM.addClass(item, '-touchplaying');
	}

	private _stopTouchAutoPlay(): void {
		// Clear interval
		if (this._touchAutoPlayInterval) {
			clearInterval(this._touchAutoPlayInterval);
			this._touchAutoPlayInterval = null;
		}

		// Stop all videos and remove all -touchplaying classes
		this._menuItems.forEach((item) => {
			const video = item.querySelector('video');
			if (video) {
				video.pause();
			}
			this.DOM.removeClass(item, '-touchplaying');
		});

		this._currentTouchPlayIndex = 0;
	}
}
